'use client'

import Link from 'next/link'
import { useState } from 'react'
import { usePathname } from 'next/navigation'
import { Menu, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { navigation } from '@/lib/content'
import { navigationEs } from '@/lib/content-es'
import { navigationFr } from '@/lib/content-fr'
import { Logo } from '@/components/logo'
import { LanguageSelector } from '@/components/language-selector'
import { getCurrentLocale } from '@/lib/utils'

export function Navigation() {
  const [isOpen, setIsOpen] = useState(false)
  const pathname = usePathname()

  // Extract current locale from pathname
  const currentLocale = getCurrentLocale(pathname)

  // Get navigation items based on current locale
  const getNavigationItems = () => {
    switch (currentLocale) {
      case 'es':
        return navigationEs
      case 'fr':
        return navigationFr
      default:
        return navigation
    }
  }

  const navItems = getNavigationItems()

  // Function to build localized href
  const getLocalizedHref = (href: string) => {
    if (href.startsWith('http')) return href
    if (/^\/(en|es|fr)(\/|$)/.test(href)) return href
    if (!currentLocale) return href
    return href === '/' ? `/${currentLocale}` : `/${currentLocale}${href}`
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <nav className="container flex h-14 max-w-screen-2xl items-center">
        <div className="mr-4 hidden md:flex">
          <Link href={getLocalizedHref('/')} className="mr-6 flex items-center space-x-2">
            <Logo />
          </Link>
          <nav className="flex items-center space-x-6 text-sm font-medium">
            {navItems.main.map((item) => {
              const isExternal = item.href.startsWith('http');
              return isExternal ? (
                <a
                  key={item.href}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-foreground/80 text-foreground/60"
                >
                  {item.name}
                </a>
              ) : (
                <Link
                  key={item.href}
                  href={getLocalizedHref(item.href)}
                  className="transition-colors hover:text-foreground/80 text-foreground/60"
                >
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
        
        <Button
          variant="ghost"
          className="mr-2 px-0 text-base hover:bg-transparent focus-visible:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 md:hidden"
          onClick={() => setIsOpen(!isOpen)}
        >
          <Menu className="h-6 w-6" />
          <span className="sr-only">Toggle Menu</span>
        </Button>
        
        <div className="flex flex-1 items-center justify-between space-x-2 md:justify-end">
          <div className="w-full flex-1 md:w-auto md:flex-none">
            <Link href={getLocalizedHref('/')} className="flex items-center md:hidden">
              <Logo />
            </Link>
          </div>
          <nav className="flex items-center space-x-2">
            <LanguageSelector />
            {navItems.cta.map((item) => (
              <Button
                key={item.href}
                variant={item.primary ? "default" : "outline"}
                size="sm"
                asChild
              >
                {item.href.startsWith('http') ? (
                  <a href={item.href} target="_blank" rel="noopener noreferrer">
                    {item.name}
                  </a>
                ) : (
                  <Link href={getLocalizedHref(item.href)}>{item.name}</Link>
                )}
              </Button>
            ))}
          </nav>
        </div>
        
        {/* Mobile menu */}
        {isOpen && (
          <div className="fixed inset-0 top-14 z-50 grid h-[calc(100vh-3.5rem)] w-full grid-flow-row auto-rows-max overflow-auto p-6 pb-32 shadow-md animate-in slide-in-from-bottom-80 md:hidden">
            <div className="relative z-20 grid gap-6 rounded-md bg-popover p-4 text-popover-foreground shadow-md">
              <nav className="grid grid-flow-row auto-rows-max text-sm">
                {navItems.main.map((item, index) => {
                  const isExternal = item.href.startsWith('http');
                  return isExternal ? (
                    <a
                      key={index}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex w-full items-center rounded-md p-2 text-sm font-medium hover:underline"
                      onClick={() => setIsOpen(false)}
                    >
                      {item.name}
                    </a>
                  ) : (
                    <Link
                      key={index}
                      href={getLocalizedHref(item.href)}
                      className="flex w-full items-center rounded-md p-2 text-sm font-medium hover:underline"
                      onClick={() => setIsOpen(false)}
                    >
                      {item.name}
                    </Link>
                  );
                })}
              </nav>
            </div>
          </div>
        )}
      </nav>
    </header>
  )
}
